import * as THREE from 'three';
import type { GameState } from '../game/state';
import { plotPosition } from '../game/grid';
import { damp } from '../utils/math';
import { GeometryBuilder, Templates as T, trs } from './GeometryBuilder';
import { groundHeight } from './Terrain';
import { LAYOUT } from './layout';
import { makeGlowTexture, toon, toonUnique } from './materials';

const BEE_MATERIAL = toonUnique({ vertexColors: true });
const _desired = new THREE.Vector3();

const BUTTERFLY_COLORS = ['#ff9fc2', '#ffd166', '#b79cff', '#7cc6fe', '#ffb38a', '#ffffff'];

interface Flyer {
  mesh: THREE.Group;
  wingL: THREE.Object3D;
  wingR: THREE.Object3D;
  target: THREE.Vector3;
  velocity: THREE.Vector3;
  phase: number;
  retarget: number;
}

interface Walker {
  mesh: THREE.Group;
  target: THREE.Vector2;
  wait: number;
  speed: number;
}

function makeButterfly(color: string): Flyer {
  const mesh = new THREE.Group();
  const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.025, 0.12, 2, 6), toon('#3d2b2b'));
  body.rotation.x = Math.PI / 2;
  mesh.add(body);
  const wingMaterial = toonUnique({ color, side: THREE.DoubleSide });
  const wingGeometry = new THREE.CircleGeometry(0.13, 10).translate(0.13, 0, 0);
  const makeWing = (side: number) => {
    const pivot = new THREE.Group();
    const upper = new THREE.Mesh(wingGeometry, wingMaterial);
    upper.rotation.x = -Math.PI / 2;
    upper.scale.set(1, 1.15, 1);
    upper.position.z = -0.03;
    const lower = new THREE.Mesh(wingGeometry, wingMaterial);
    lower.rotation.x = -Math.PI / 2;
    lower.scale.set(0.7, 0.7, 1);
    lower.position.z = 0.07;
    pivot.add(upper, lower);
    pivot.scale.x = side;
    mesh.add(pivot);
    return pivot;
  };
  return {
    mesh,
    wingL: makeWing(-1),
    wingR: makeWing(1),
    target: new THREE.Vector3(),
    velocity: new THREE.Vector3(),
    phase: Math.random() * 10,
    retarget: 0,
  };
}

function makeBee(): Flyer {
  const mesh = new THREE.Group();
  const b = new GeometryBuilder();
  b.add(T.sphere, '#ffd23f', trs([0, 0, 0], [0, 0, 0], [0.07, 0.065, 0.1]));
  // Two dark stripes wrapped around the body (cylinders turned to lie along z).
  b.add(T.cylinder, '#3d2b2b', trs([0, 0, -0.005], [Math.PI / 2, 0, 0], [0.071, 0.03, 0.066]));
  b.add(T.cylinder, '#3d2b2b', trs([0, 0, -0.06], [Math.PI / 2, 0, 0], [0.058, 0.025, 0.054]));
  mesh.add(new THREE.Mesh(b.build().base, BEE_MATERIAL));
  const wingMaterial = new THREE.MeshBasicMaterial({
    color: '#ffffff',
    transparent: true,
    opacity: 0.75,
    side: THREE.DoubleSide,
  });
  const wing = new THREE.CircleGeometry(0.06, 8).translate(0.06, 0, 0);
  const wingL = new THREE.Mesh(wing, wingMaterial);
  const wingR = new THREE.Mesh(wing, wingMaterial);
  wingL.rotation.x = wingR.rotation.x = -Math.PI / 2;
  wingL.position.y = wingR.position.y = 0.06;
  wingL.scale.x = -1;
  mesh.add(wingL, wingR);
  return {
    mesh,
    wingL,
    wingR,
    target: new THREE.Vector3(),
    velocity: new THREE.Vector3(),
    phase: Math.random() * 10,
    retarget: 0,
  };
}

function makeSheep(): THREE.Group {
  const sheep = new THREE.Group();
  const b = new GeometryBuilder();
  for (const [x, y, z, s] of [
    [0, 0.75, 0, 0.5],
    [0.28, 0.8, 0.1, 0.36],
    [-0.28, 0.8, -0.05, 0.38],
    [0, 0.95, 0.2, 0.34],
    [0.05, 0.92, -0.25, 0.34],
  ] as const) {
    b.add(T.ico1, '#ffffff', trs([x, y, z], [0, 0, 0], s), { color2: '#f2eee8' });
  }
  b.add(T.sphere, '#4a4048', trs([0, 0.88, 0.55], [0, 0, 0], [0.22, 0.24, 0.24]));
  b.add(T.sphere, '#4a4048', trs([0.2, 0.95, 0.52], [0, 0, 0.6], [0.12, 0.05, 0.07]));
  b.add(T.sphere, '#4a4048', trs([-0.2, 0.95, 0.52], [0, 0, -0.6], [0.12, 0.05, 0.07]));
  b.add(T.sphere, '#ffffff', trs([0.08, 0.93, 0.74], [0, 0, 0], 0.035));
  b.add(T.sphere, '#ffffff', trs([-0.08, 0.93, 0.74], [0, 0, 0], 0.035));
  for (const [x, z] of [
    [0.22, 0.25],
    [-0.22, 0.25],
    [0.22, -0.25],
    [-0.22, -0.25],
  ] as const) {
    b.add(T.cylinder, '#4a4048', trs([x, 0, z], [0, 0, 0], [0.06, 0.45, 0.06]));
  }
  const mesh = new THREE.Mesh(b.build().base, toonUnique({ vertexColors: true }));
  mesh.castShadow = true;
  sheep.add(mesh);
  return sheep;
}

function makeDuck(): THREE.Group {
  const duck = new THREE.Group();
  const b = new GeometryBuilder();
  b.add(T.sphere, '#fffaf0', trs([0, 0.15, 0], [0, 0, 0], [0.22, 0.16, 0.3]));
  b.add(T.sphere, '#fffaf0', trs([0, 0.42, 0.18], [0, 0, 0], 0.13));
  b.add(T.cone, '#ff9f43', trs([0, 0.4, 0.3], [Math.PI / 2, 0, 0], [0.05, 0.12, 0.03]));
  b.add(T.sphere, '#2b2340', trs([0.07, 0.45, 0.27], [0, 0, 0], 0.02));
  b.add(T.sphere, '#2b2340', trs([-0.07, 0.45, 0.27], [0, 0, 0], 0.02));
  b.add(T.sphere, '#f2f2e8', trs([0, 0.22, -0.28], [0.6, 0, 0], [0.08, 0.04, 0.1]));
  duck.add(new THREE.Mesh(b.build().base, toonUnique({ vertexColors: true })));
  return duck;
}

/** Ambient life: butterflies, bees, fireflies, sheep and ducks. */
export class Critters {
  readonly group = new THREE.Group();
  private readonly butterflies: Flyer[] = [];
  private readonly bees: Flyer[] = [];
  private readonly sheep: Walker[] = [];
  private readonly ducks: THREE.Group[] = [];
  private readonly fireflies: THREE.Points<THREE.BufferGeometry, THREE.PointsMaterial>;
  private readonly fireflySeeds: Float32Array;

  constructor(private readonly beehive: THREE.Vector3) {
    for (let i = 0; i < 10; i++) {
      const fly = makeButterfly(BUTTERFLY_COLORS[i % BUTTERFLY_COLORS.length]!);
      fly.mesh.position.set((Math.random() - 0.5) * 20, 1.5, (Math.random() - 0.5) * 20);
      this.butterflies.push(fly);
      this.group.add(fly.mesh);
    }
    for (let i = 0; i < 9; i++) {
      const bee = makeBee();
      bee.mesh.position.copy(beehive);
      bee.mesh.visible = false;
      this.bees.push(bee);
      this.group.add(bee.mesh);
    }
    for (let i = 0; i < 5; i++) {
      const mesh = makeSheep();
      const p = this.pastureSpot();
      mesh.position.set(p.x, groundHeight(p.x, p.y), p.y);
      mesh.rotation.y = Math.random() * 6.28;
      mesh.scale.setScalar(1.3);
      this.sheep.push({
        mesh,
        target: this.pastureSpot(),
        wait: Math.random() * 5,
        speed: 0.8 + Math.random() * 0.4,
      });
      this.group.add(mesh);
    }
    for (let i = 0; i < 2; i++) {
      const duck = makeDuck();
      this.ducks.push(duck);
      this.group.add(duck);
    }

    const count = 70;
    this.fireflySeeds = new Float32Array(count * 4);
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      this.fireflySeeds.set(
        [
          (Math.random() - 0.5) * 46,
          0.4 + Math.random() * 2.6,
          (Math.random() - 0.5) * 40,
          Math.random() * 100,
        ],
        i * 4,
      );
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.fireflies = new THREE.Points(
      geometry,
      new THREE.PointsMaterial({
        size: 0.45,
        map: makeGlowTexture(),
        color: '#f6ff8a',
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    this.fireflies.frustumCulled = false;
    this.group.add(this.fireflies);
  }

  private pastureSpot(): THREE.Vector2 {
    const a = Math.random() * Math.PI * 2;
    const r = Math.sqrt(Math.random()) * LAYOUT.pasture.r;
    return new THREE.Vector2(
      LAYOUT.pasture.x + Math.cos(a) * r,
      LAYOUT.pasture.z + Math.sin(a) * r,
    );
  }

  update(dt: number, time: number, state: GameState, night: number): void {
    const day = night < 0.6;
    const bloomed = state.plots.filter((p) => p.crop && p.crop.growth >= 1).map((p) => p.id);

    // Butterflies flutter between blooming plots (or wander the yard).
    for (const fly of this.butterflies) {
      fly.mesh.visible = day;
      if (!day) continue;
      fly.retarget -= dt;
      if (fly.retarget <= 0) {
        fly.retarget = 2 + Math.random() * 4;
        if (bloomed.length > 0 && Math.random() < 0.7) {
          const { x, z } = plotPosition(bloomed[Math.floor(Math.random() * bloomed.length)]!);
          fly.target.set(
            x + (Math.random() - 0.5),
            0.9 + Math.random() * 0.6,
            z + (Math.random() - 0.5),
          );
        } else {
          fly.target.set(
            (Math.random() - 0.5) * 34,
            1.2 + Math.random() * 2,
            (Math.random() - 0.5) * 30,
          );
        }
      }
      this.steer(fly, dt, 2.2, time);
      const flap = Math.sin(time * 16 + fly.phase) * 0.9;
      fly.wingL.rotation.z = flap;
      fly.wingR.rotation.z = -flap;
    }

    // Bees appear with the beehive upgrade and visit blooming flowers.
    const beeCount = state.upgrades.beehive * 3;
    this.bees.forEach((bee, i) => {
      bee.mesh.visible = day && i < beeCount;
      if (!bee.mesh.visible) return;
      bee.retarget -= dt;
      if (bee.retarget <= 0) {
        bee.retarget = 1.5 + Math.random() * 2.5;
        if (bloomed.length > 0 && Math.random() < 0.75) {
          const { x, z } = plotPosition(bloomed[Math.floor(Math.random() * bloomed.length)]!);
          bee.target.set(
            x + (Math.random() - 0.5) * 0.8,
            0.8 + Math.random() * 0.4,
            z + (Math.random() - 0.5) * 0.8,
          );
        } else {
          bee.target
            .copy(this.beehive)
            .add(
              new THREE.Vector3(
                (Math.random() - 0.5) * 2,
                Math.random(),
                (Math.random() - 0.5) * 2,
              ),
            );
        }
      }
      this.steer(bee, dt, 3.5, time);
      const flap = Math.sin(time * 60 + bee.phase) * 0.6;
      bee.wingL.rotation.z = flap;
      bee.wingR.rotation.z = -flap;
    });

    // Sheep graze lazily in the pasture beyond the fence.
    for (const sheep of this.sheep) {
      const pos = sheep.mesh.position;
      if (sheep.wait > 0) {
        sheep.wait -= dt;
        sheep.mesh.children[0]!.rotation.x = Math.sin(time * 2) * 0.03 + 0.05;
        continue;
      }
      const dx = sheep.target.x - pos.x;
      const dz = sheep.target.y - pos.z;
      const dist = Math.hypot(dx, dz);
      if (dist < 0.3) {
        sheep.wait = 3 + Math.random() * 8;
        sheep.target = this.pastureSpot();
        continue;
      }
      const heading = Math.atan2(dx, dz);
      sheep.mesh.rotation.y +=
        Math.atan2(
          Math.sin(heading - sheep.mesh.rotation.y),
          Math.cos(heading - sheep.mesh.rotation.y),
        ) * damp(4, dt);
      pos.x += Math.sin(sheep.mesh.rotation.y) * sheep.speed * dt;
      pos.z += Math.cos(sheep.mesh.rotation.y) * sheep.speed * dt;
      pos.y = groundHeight(pos.x, pos.z) + Math.abs(Math.sin(time * 8)) * 0.08;
      sheep.mesh.children[0]!.rotation.x = 0;
    }

    // Ducks paddle in slow circles around the pond.
    this.ducks.forEach((duck, i) => {
      const a = time * 0.18 * (i === 0 ? 1 : -0.8) + i * 2.4;
      const r = LAYOUT.pond.r * (0.45 + i * 0.2);
      duck.position.set(
        LAYOUT.pond.x + Math.cos(a) * r,
        0.04 + Math.sin(time * 2 + i) * 0.02,
        LAYOUT.pond.z + Math.sin(a) * r,
      );
      duck.rotation.y = -a + (i === 0 ? Math.PI : 0);
    });

    // Fireflies twinkle around the homestead at night.
    this.fireflies.visible = night > 0.05;
    if (this.fireflies.visible) {
      const pos = this.fireflies.geometry.getAttribute('position') as THREE.BufferAttribute;
      const n = pos.count;
      for (let i = 0; i < n; i++) {
        const [bx, by, bz, seed] = [
          this.fireflySeeds[i * 4]!,
          this.fireflySeeds[i * 4 + 1]!,
          this.fireflySeeds[i * 4 + 2]!,
          this.fireflySeeds[i * 4 + 3]!,
        ];
        pos.setXYZ(
          i,
          bx + Math.sin(time * 0.4 + seed) * 1.8,
          by + Math.sin(time * 0.9 + seed * 2) * 0.4,
          bz + Math.cos(time * 0.35 + seed) * 1.8,
        );
      }
      pos.needsUpdate = true;
      this.fireflies.material.opacity = night * (0.6 + Math.sin(time * 3) * 0.2);
    }
  }

  private steer(fly: Flyer, dt: number, speed: number, time: number): void {
    const desired = _desired.copy(fly.target).sub(fly.mesh.position);
    const dist = desired.length();
    desired.normalize().multiplyScalar(Math.min(speed, dist * 1.5));
    fly.velocity.lerp(desired, damp(2.5, dt));
    fly.mesh.position.addScaledVector(fly.velocity, dt);
    fly.mesh.position.y += Math.sin(time * 5 + fly.phase) * 0.006;
    if (fly.velocity.lengthSq() > 0.01)
      fly.mesh.rotation.y = Math.atan2(fly.velocity.x, fly.velocity.z);
  }
}
